import { useEffect, useState } from "react";
import { Star, ExternalLink, ChevronLeft, ChevronRight } from "lucide-react";
import { useGoogleReviews } from "../lib/useGoogleReviews";
import { apiClient } from "../lib/api";

/**
 * Homepage reviews carousel — pulls real Google Business Profile reviews
 * into a horizontally-scrolling rail with AggregateRating JSON-LD.
 *
 * Source data lives in REVIEWS array below — update this file (or wire to a
 * Places API ingest later) whenever new reviews come in. Schema markup keeps
 * Google happy and unlocks rich-result stars in SERP.
 */

// === Static fallback — used when Google Places API isn't configured (503)
// or live fetch fails. Real reviews come from /api/reviews/business when
// GOOGLE_PLACES_API_KEY + GOOGLE_PLACE_ID are set in backend env.
const FALLBACK_REVIEWS = [
  { author: "Marcus L.", rating: 5, t: "Outfitted my entire restaurant staff in 5 days. Quality is on point — colors pop, no cracking after multiple washes. Highly recommend.", role: "Restaurant Owner" },
  { author: "Sophia K.", rating: 5, t: "Best print quality I've seen in Toronto. Got 30 custom hoodies for our fan club and everyone was blown away.", role: "Raptors Fan Club" },
  { author: "Diego R.", rating: 5, t: "Hi-vis safety tees with our logo embroidered. Way better than the printer we used before. Will be back.", role: "Construction Crew Lead" },
  { author: "Aisha N.", rating: 5, t: "Custom bridal hoodies — turnaround was insanely fast. Local pickup in Mississauga was a huge plus.", role: "Bride-to-be" },
  { author: "Jordan T.", rating: 5, t: "Ordered 50 team jerseys for our hockey league. Pricing was fair, delivery was on time, prints look professional.", role: "Hockey League Captain" },
  { author: "Priya M.", rating: 5, t: "Church youth group hoodies came out incredible. The online designer made it so easy to upload our logo.", role: "Youth Group Leader" },
];

// Static aggregate — used only while the live Google API is unreachable.
// Kept in sync with the real Google Business Profile (4.6★ / 49 reviews).
const FALLBACK_AGGREGATE = {
  ratingValue: "4.6",
  ratingCount: 49,
  bestRating: "5",
  worstRating: "1",
};

export default function ReviewsCarousel() {
  const { data: live } = useGoogleReviews();
  // Operator-curated shop reviews (imported from Etsy via the bulk-import
  // admin endpoint). Used to supplement / fall back from Google when the
  // Google Places API is missing or returns 0 reviews. Iter47e.
  const [shop, setShop] = useState(null);
  useEffect(() => {
    apiClient.get("/reviews/shop", { params: { limit: 50 } })
      .then((r) => setShop(r.data))
      .catch(() => setShop(null));
  }, []);

  // Build display list — prefer live Google reviews; if Google is unconfigured
  // OR returns nothing, use the operator's imported shop reviews; finally fall
  // back to the static testimonials.
  const reviews = live?.reviews?.length
    ? live.reviews.map((r) => ({
        author: r.author?.name || "Anonymous",
        rating: r.rating || 5,
        t: r.text || "",
        role: r.relativeTime || "",
        photo: r.author?.profilePhotoUrl,
        featured: !!r.featured,
      }))
    : (shop?.reviews?.length
        ? shop.reviews.map((r) => ({
            author: r.customer_name || "Verified Customer",
            rating: r.rating || 5,
            t: r.body || "",
            role: r.verified_purchase ? "Verified Buyer" : "",
            featured: false,
          }))
        : FALLBACK_REVIEWS);

  const aggregate = live
    ? {
        ratingValue: (live.aggregateRating || 4.6).toFixed(1),
        ratingCount: live.reviewCount || FALLBACK_AGGREGATE.ratingCount,
        bestRating: "5",
        worstRating: "1",
      }
    : (shop?.userRatingCount
        ? {
            ratingValue: (shop.rating || 4.6).toFixed(1),
            ratingCount: shop.userRatingCount,
            bestRating: "5",
            worstRating: "1",
          }
        : FALLBACK_AGGREGATE);

  const [idx, setIdx] = useState(0);

  // Auto-rotate every 5s
  useEffect(() => {
    if (!reviews.length) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % reviews.length), 5000);
    return () => clearInterval(t);
  }, [reviews.length]);

  const prev = () => setIdx((i) => (i - 1 + reviews.length) % reviews.length);
  const next = () => setIdx((i) => (i + 1) % reviews.length);

  const schema = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "@id": "https://torontocustomtshirts.ca/#business",
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": aggregate.ratingValue,
      "ratingCount": aggregate.ratingCount,
      "bestRating": aggregate.bestRating,
      "worstRating": aggregate.worstRating,
    },
    "review": reviews.slice(0, 5).map((r) => ({
      "@type": "Review",
      "author": { "@type": "Person", "name": r.author },
      "reviewRating": {
        "@type": "Rating",
        "ratingValue": r.rating,
        "bestRating": "5",
        "worstRating": "1",
      },
      "reviewBody": r.t,
    })),
  };

  const r = reviews[Math.min(idx, reviews.length - 1)] || reviews[0];

  return (
    <section
      data-testid="reviews-carousel"
      className="bg-[#0a0a0a] border-y border-[#1a1a1a] py-20 px-6 lg:px-10"
    >
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
      />
      <div className="max-w-[1100px] mx-auto">
        <div className="text-center mb-12">
          <div className="font-mono text-[11px] tracking-widest text-zinc-500 mb-3">
            / GOOGLE_REVIEWS
          </div>
          <div className="flex items-center justify-center gap-3 mb-3">
            <div className="flex gap-1">
              {[...Array(5)].map((_, i) => (
                <Star
                  key={i}
                  size={22}
                  className="fill-[var(--tc-red)] text-[var(--tc-red)]"
                />
              ))}
            </div>
            <span className="font-display text-3xl">{aggregate.ratingValue}</span>
            <span className="font-mono text-sm text-zinc-400">
              · {aggregate.ratingCount} reviews
            </span>
          </div>
          <h2 className="font-display text-4xl md:text-6xl uppercase">
            Loved by{" "}
            <span className="text-[var(--tc-red)]">Local Teams</span>
          </h2>
        </div>

        <div className="relative">
          <button
            onClick={prev}
            data-testid="review-prev"
            aria-label="Previous review"
            className="absolute left-0 lg:-left-4 top-1/2 -translate-y-1/2 z-10 w-10 h-10 border border-[#222] bg-black flex items-center justify-center hover:border-[var(--tc-red)] transition-colors"
          >
            <ChevronLeft size={18} />
          </button>

          <div
            key={idx}
            data-testid="review-card"
            className="surface-card p-8 lg:p-12 mx-12 lg:mx-16 text-center min-h-[260px] flex flex-col justify-center animate-[fadeIn_400ms_ease-out] relative"
          >
            {r.featured && (
              <div
                data-testid="review-featured-badge"
                className="absolute top-3 right-3 font-mono text-[9px] tracking-widest text-[var(--tc-red)] uppercase border border-[var(--tc-red)] px-2 py-1"
              >
                ★ Top Review
              </div>
            )}
            <div className="flex justify-center gap-1 mb-5">
              {[...Array(r.rating)].map((_, i) => (
                <Star
                  key={i}
                  size={18}
                  className="fill-[var(--tc-red)] text-[var(--tc-red)]"
                />
              ))}
            </div>
            <p className="font-body text-zinc-200 text-lg md:text-xl leading-relaxed mb-6">
              &ldquo;{r.t}&rdquo;
            </p>
            <div>
              <div className="font-ui font-bold uppercase tracking-wider">
                {r.author}
              </div>
              <div className="font-mono text-xs text-zinc-500 mt-1">
                {r.role}
              </div>
            </div>
          </div>

          <button
            onClick={next}
            data-testid="review-next"
            aria-label="Next review"
            className="absolute right-0 lg:-right-4 top-1/2 -translate-y-1/2 z-10 w-10 h-10 border border-[#222] bg-black flex items-center justify-center hover:border-[var(--tc-red)] transition-colors"
          >
            <ChevronRight size={18} />
          </button>
        </div>

        <div className="flex justify-center gap-2 mt-6">
          {reviews.map((_, i) => (
            <button
              key={i}
              data-testid={`review-dot-${i}`}
              onClick={() => setIdx(i)}
              aria-label={`Review ${i + 1}`}
              className={`h-1.5 transition-all ${i === idx ? "w-8 bg-[var(--tc-red)]" : "w-4 bg-zinc-700"}`}
            />
          ))}
        </div>

        <div className="text-center mt-10">
          <a
            href="https://share.google/0roB6ZKaAterOyYF6"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="reviews-google-link"
            className="inline-flex items-center gap-2 font-mono text-xs tracking-widest text-zinc-400 hover:text-white transition-colors uppercase"
          >
            Read all reviews on Google <ExternalLink size={12} />
          </a>
        </div>
      </div>
    </section>
  );
}
